import React, { useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import api from '@utils/axios';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { TextArea } from '@progress/kendo-react-inputs';
import { Button } from '@ui';
import { translate } from '@utils/i18n';
import { updateStatusesDeficienciesPath } from 'routes';
import ColumnMenuContext from './ColumnMenuContext.tsx';

const namespace = 'features.deficiencies_index.update_modal';
const BATCH_SIZE = 10;

function UpdateModal({ deficiencyIds, disabled, queryParams, deficienciesCount }) {
  const { deficiencyStatusesForSelect, resolutionStatusesForSelect, afterSubmit } = useContext(ColumnMenuContext);

  const defaultResolved = { label: translate('select_resolved', { namespace }), value: null };
  const defaultDeficiencyStatus = { label: translate('select_status', { namespace }), value: null };
  const defaultResolutionStatus = { label: translate('select_resolution_status', { namespace }), value: null };

  const [visible, setVisible] = useState(false);
  const [isResolved, setIsResolved] = useState(defaultResolved);
  const [deficiencyStatus, setDeficiencyStatus] = useState(defaultDeficiencyStatus);
  const [resolutionStatus, setResolutionStatus] = useState(defaultResolutionStatus);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [deficienciesProcessed, setDeficienciesProcessed] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const shouldContinueRef = useRef(true);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const clearForm = () => {
    shouldContinueRef.current = false;
    toggleDialog();
    setIsResolved(defaultResolved);
    setDeficiencyStatus(defaultDeficiencyStatus);
    setResolutionStatus(defaultResolutionStatus);
    setResolutionNotes('');
    setDeficienciesProcessed(0);
    setIsProcessing(false);
  };

  const buildFormData = () => {
    const formData = {
      is_resolved: isResolved.value && '1',
      deficiency_resolution_status_id: resolutionStatus.value,
      deficiency_status: deficiencyStatus.value
    };

    if (resolutionNotes && resolutionNotes.trim() !== '') {
      formData.resolution_notes = resolutionNotes.trim();
    }

    Object.keys(formData).forEach((key) => {
      if (formData[key] === null) {
        delete formData[key];
      }
    });

    return formData;
  };

  const batchUpdate = (formData, ids) => {
    if (!shouldContinueRef.current) {
      setIsProcessing(false);
      return;
    }

    if (!ids.length) {
      setIsProcessing(false);
      afterSubmit();
      toast.success(translate('success', { namespace }));
      return;
    }

    const batch = ids.splice(0, BATCH_SIZE);

    api
      .post(`${updateStatusesDeficienciesPath({ format: 'json' })}?${queryParams}`, {
        ...formData,
        deficiency_status_update_ids: batch.join(',')
      })
      .then(({ data }) => {
        if (!shouldContinueRef.current) {
          setIsProcessing(false);
          return;
        }
        setDeficienciesProcessed((prev) => prev + data.ids.length);
        batchUpdate(formData, ids);
      })
      .catch(({ response }) => {
        if (!shouldContinueRef.current) {
          setIsProcessing(false);
          return;
        }
        const errorMessages = response?.data?.errors ?? [translate('error', { namespace })];
        errorMessages.forEach((msg) => toast.error(msg));
        setDeficienciesProcessed((prev) => prev + batch.length);
        batchUpdate(formData, ids);
      });
  };

  const onSubmit = () => {
    shouldContinueRef.current = true;
    setDeficienciesProcessed(0);
    setIsProcessing(true);

    const formData = buildFormData();

    if (!deficiencyIds) {
      api
        .post(`${updateStatusesDeficienciesPath({ format: 'json' })}?${queryParams}`, { all: true })
        .then(({ data }) => {
          batchUpdate(formData, data);
        })
        .catch(({ response }) => {
          const errorMessages = response?.data?.errors ?? [translate('error', { namespace })];
          errorMessages.forEach((msg) => toast.error(msg));
          setIsProcessing(false);
        });
    } else {
      batchUpdate(formData, deficiencyIds.split(','));
    }
  };

  return (
    <>
      <button type="button" tabIndex="0" className="qmb-control" disabled={disabled} onClick={toggleDialog}>
        <i className="fa-light fa-pen-to-square" />
        {translate('button', { namespace })}
      </button>
      {visible && (
        <Dialog title={translate('title', { namespace })} className="qmb-dialog" onClose={toggleDialog}>
          <p>{translate('subtitle', { namespace, count: deficienciesCount })}</p>
          <table className="qmb-table--spreadsheet">
            <thead>
              <tr>
                <th>{translate('change', { namespace })}</th>
                <th>{translate('value', { namespace })}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="nested_fields">
                <td className="table__cell" aria-label={translate('resolved', { namespace })}>
                  <div className="qmb-input--text">
                    <input value={translate('resolved', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="resolved"
                      textField="label"
                      dataItemKey="value"
                      data={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false }
                      ]}
                      value={isResolved}
                      defaultItem={defaultResolved}
                      onChange={(event) => setIsResolved(event.target.value)}
                      size="large"
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell" aria-label={translate('resolution_status', { namespace })}>
                  <div className="qmb-input--text">
                    <input value={translate('resolution_status', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="resolutionStatus"
                      textField="label"
                      dataItemKey="value"
                      data={resolutionStatusesForSelect}
                      value={resolutionStatus}
                      defaultItem={defaultResolutionStatus}
                      onChange={(event) => setResolutionStatus(event.target.value)}
                      size="large"
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell" aria-label={translate('status', { namespace })}>
                  <div className="qmb-input--text">
                    <input value={translate('status', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="deficiencyStatus"
                      textField="label"
                      dataItemKey="value"
                      data={deficiencyStatusesForSelect}
                      value={deficiencyStatus}
                      defaultItem={defaultDeficiencyStatus}
                      onChange={(event) => setDeficiencyStatus(event.target.value)}
                      size="large"
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell" aria-label={translate('resolution_notes', { namespace })}>
                  <div className="qmb-input--text">
                    <input value={translate('resolution_notes', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <TextArea
                      name="resolutionNotes"
                      rows={4}
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.value)}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="modal__footer mt-3 pull-right" style={{ justifyContent: 'space-between' }}>
            {deficienciesProcessed > 0 && (
              <div>
                Processing: {deficienciesProcessed}/{deficienciesCount}
              </div>
            )}
            <Button onClick={clearForm}>{translate('cancel', { namespace: 'features.commons' })}</Button>
            <Button variant="primary" onClick={onSubmit} disabled={isProcessing}>
              {translate('update', { namespace: 'features.commons' })}
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
}

UpdateModal.propTypes = {
  deficiencyIds: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool.isRequired,
  queryParams: PropTypes.object.isRequired,
  deficienciesCount: PropTypes.number.isRequired
};

UpdateModal.defaultProps = {
  deficiencyIds: null
};

export default UpdateModal;
